import { useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  LedgerAccountFragment,
  VendorWithLedgerAccountResourcesFragment,
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  LedgerAccountSelector,
  formatAccountName,
} from 'modules/banking/components/LedgerAccountSelector'
import { useUpsertLedgerAccountResource } from 'modules/finance/lib/accounting/useUpsertLedgerAccountResource'
import { contactFullName } from 'modules/finance/lib/contactsUtils'
import type { LedgerAccountsLegalEntity } from 'modules/finance/pages/Accounting/AccountsPage'

interface Props {
  vendor: VendorWithLedgerAccountResourcesFragment
  legalEntity: LedgerAccountsLegalEntity
  accounts: LedgerAccountFragment[]
  canManage: boolean
}
export const VendorRow = ({ vendor, legalEntity, accounts, canManage }: Props) => {
  const accountResource = vendor.accountResources.find(
    (resource) => resource.operationType == 'purchase'
  )
  const accountingResourceId = accountResource?.id
  const accountId = accountResource?.account?.id

  const { upsertLedgerAccountResource, loading: savingLedgerAccount } =
    useUpsertLedgerAccountResource()

  const [selectedAccountId, setSelectedAccountId] = useState<string>()
  useEffect(() => {
    if (accountId) setSelectedAccountId(String(accountId))
  }, [accountId])

  const linkAccount = useUnsafeCallback(
    async (newAccountId: string) => {
      const result = await upsertLedgerAccountResource({
        id: accountingResourceId,
        legalEntityId: legalEntity.id,
        accountId: newAccountId,
        resourceId: vendor.id,
        resourceType: 'vendor',
      }).then((res) => {
        track(trackingEvents.accounting.settings.vendors.editLedgerAccount)
        return res
      })

      const handled = handleResult(result, () => undefined)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
          time: 2000,
        })
      }
    },
    [upsertLedgerAccountResource, accountingResourceId, legalEntity.id, vendor.id]
  )

  const onSelect = useUnsafeCallback(
    (id) => {
      setSelectedAccountId(id)
      linkAccount(id)
    },
    [setSelectedAccountId, linkAccount]
  )

  const account = useUnsafeMemo(
    () => accounts.find((a) => a.id == selectedAccountId),
    [accounts, selectedAccountId]
  )

  return (
    <Box padding="s0" paddingTop={'s32'}>
      <Box flexDirection="row" alignItems="flexStart" justifyContent="spaceBetween" gap="s16">
        <Box width="s600">
          <Text size={'300'} weight="semibold">
            {contactFullName(vendor)}
          </Text>
        </Box>
        <Box width="full">
          <LedgerAccountSelector
            legalEntityId={legalEntity.id}
            currentLedgerAccountName={formatAccountName(account)}
            accounts={accounts}
            canManage={canManage}
            disabled={savingLedgerAccount}
            onSelect={onSelect}
          />
        </Box>
      </Box>
    </Box>
  )
}
